import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Video {
  id: number;
  title: string;
  creator: string;
  avatar: string;
  views: number;
  thumbnail: string;
  duration: string;
  uploadedAt: string;
  likes: number;
}

export default function UploadedVideos() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      const { data, error } = await supabase
        .from('videos')
        .select(`
          *,
          profiles!videos_user_id_fkey (
            username,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Error fetching videos:', error);
      } else {
        setVideos(data || []);
      }
      setLoading(false);
    };

    fetchVideos();
  }, []);

  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'trending'>('recent');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Video Library</h2>
          <p className="text-gray-400">Watch uploaded matches, tutorials, and highlights</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setSortBy('recent')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'recent'
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Recent
          </button>
          <button
            onClick={() => setSortBy('popular')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'popular'
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Popular
          </button>
          <button
            onClick={() => setSortBy('trending')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'trending'
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Trending
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <div
            key={video.id}
            className="bg-gray-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-red-600 transition-all cursor-pointer group"
          >
            <div className="relative">
              <div className="w-full h-48">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute bottom-3 right-3 bg-black/90 text-white px-2 py-1 rounded text-sm font-semibold">
                {video.duration}
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:scale-110">
                  <i className="ri-play-fill text-white text-3xl"></i>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img
                    src={video.avatar}
                    alt={video.creator}
                    className="w-full h-full rounded-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold mb-1 group-hover:text-red-400 transition-colors line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">{video.creator}</p>
                  <div className="flex items-center gap-4 text-gray-500 text-sm">
                    <span className="flex items-center">
                      <i className="ri-eye-line mr-1"></i>
                      {video.views.toLocaleString()}
                    </span>
                    <span className="flex items-center">
                      <i className="ri-heart-line mr-1"></i>
                      {video.likes.toLocaleString()}
                    </span>
                    <span>{video.uploadedAt}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
